import { saveGame } from '../services/gameService';
import { extractPlayerStatsFromBoxScore, savePlayerStats } from '../services/playerStatService';

const STORAGE_KEY = 'redsox_game_history';
const SITEMAP_KEY = 'redsox_sitemap';

export const getStoredGames = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const makeGameSlug = (game) => {
  const date = game?.officialDate || (game?.gameDate ? game.gameDate.split('T')[0] : null);
  const away = (game?.teams?.away?.team?.abbreviation || 'away').toLowerCase().replace(/[^a-z0-9]/g, '');
  const home = (game?.teams?.home?.team?.abbreviation || 'home').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!date) return null;
  return `${date}-${away}-at-${home}`;
};

export const generateSitemap = (games) => {
  const base = 'https://redsox.website';
  const now = new Date().toISOString().split('T')[0];
  
  const urls = [
    `<url><loc>${base}/</loc><changefreq>always</changefreq><priority>1.0</priority><lastmod>${now}</lastmod></url>`,
    `<url><loc>${base}/#/history</loc><changefreq>daily</changefreq><priority>0.8</priority><lastmod>${now}</lastmod></url>`,
    `<url><loc>${base}/#/franchise</loc><changefreq>weekly</changefreq><priority>0.9</priority><lastmod>${now}</lastmod></url>`,
    `<url><loc>${base}/sitemap.xml</loc><changefreq>daily</changefreq><priority>0.3</priority><lastmod>${now}</lastmod></url>`,
  ];

  games.forEach((g) => {
    const d = g.officialDate || (g.date ? g.date.split('T')[0] : now);
    if (g.slug) {
      urls.push(`<url><loc>${base}/#/game/${g.slug}</loc><lastmod>${d}</lastmod><changefreq>never</changefreq><priority>0.6</priority></url>`);
    }
    urls.push(`<url><loc>${base}/#/game/pk/${g.gamePk}</loc><lastmod>${d}</lastmod><changefreq>never</changefreq><priority>0.5</priority></url>`);
  });

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:news="http://www.google.com/schemas/sitemap-news/0.9">\n${urls.join('\n')}\n</urlset>`;
};

// Exported so we can call it manually to backfill data
export const syncToSheets = async (entry, analysis) => {
  try {
    const generateId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2);
    const gameId = entry.id || generateId();
    
    await saveGame({
      id: gameId,
      gamePk: entry.gamePk,
      slug: entry.slug,
      date: entry.officialDate || entry.date,
      venue: entry.venue,
      away: entry.away,
      home: entry.home,
      decisions: entry.decisions,
      status: 'Final',
      savedAt: entry.savedAt,
    });

    const playerStats = extractPlayerStatsFromBoxScore(
      analysis?.boxScore || entry.boxScore, 
      entry.gamePk, 
      entry.officialDate || entry.date
    );

    if (playerStats.length > 0) {
      await savePlayerStats(playerStats, entry.gamePk);
    }
  } catch (err) {
    console.error('Google Sheets sync failed:', err);
  }
};

export const saveCompletedGame = (game, analysis, recap) => {
  try {
    const games = getStoredGames();
    const gamePk = game?.gamePk;
    if (!gamePk) return;

    // Check if it exists in local storage
    const exists = games.find((g) => g.gamePk === gamePk);
    
    const away = game?.teams?.away;
    const home = game?.teams?.home;
    const slug = makeGameSlug(game);
    const officialDate = game?.officialDate || (game?.gameDate ? game.gameDate.split('T')[0] : null);

    const entry = exists || {
      gamePk,
      slug,
      officialDate,
      date: game?.gameDate,
      venue: game?.venue?.name,
      away: {
        name: away?.team?.name,
        abbr: away?.team?.abbreviation,
        score: away?.score,
        record: away?.leagueRecord,
      },
      home: {
        name: home?.team?.name,
        abbr: home?.team?.abbreviation,
        score: home?.score,
        record: home?.leagueRecord,
      },
      decisions: game?.decisions,
      innings: analysis?.linescoreData?.innings || [],
      allPlays: analysis?.playByPlay?.allPlays || [],
      boxScore: analysis?.boxScore || null,
      feed: recap?.feed || null,
      savedAt: new Date().toISOString(),
    };

    if (!exists) {
      games.unshift(entry);
      const trimmed = games.slice(0, 100);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
      
      const sitemapXml = generateSitemap(trimmed);
      localStorage.setItem(SITEMAP_KEY, sitemapXml);
    }

    // Always attempt to sync to sheets (safe because duplicates are now prevented)
    syncToSheets(entry, analysis);

    return entry;
  } catch (err) {
    console.error('Failed to save game:', err);
  }
};

export const deleteGame = (gamePk) => {
  const games = getStoredGames();
  const updatedGames = games.filter((g) => g.gamePk !== gamePk);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedGames));
  
  const sitemapXml = generateSitemap(updatedGames);
  localStorage.setItem(SITEMAP_KEY, sitemapXml);
};

export const serveSitemapXml = (games) => {
  try {
    const storedSitemap = localStorage.getItem(SITEMAP_KEY);
    if (storedSitemap) {
      return storedSitemap;
    }
  } catch (err) {
    // Ignore and fallback to generating
  }
  return generateSitemap(games);
};